// src/helpers/schedule/awp.js
// ENHANCED: extracts all geometry, assigns robust names, maps every Revit
// category to BidLight AWP codes, and sequences tasks in a realistic order
// (e.g., walls before windows & doors, concrete before steel, etc.).

import { HOURS_PER_DAY, sum } from './config';

/* ---------------------------- tiny helpers ---------------------------- */


const norm = (v) =>
  (v ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^a-z0-9 _\-./|]+/g, '');

const key = (...parts) => parts.map(norm).filter(Boolean).join('|');
const first = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : null);

/* ---------------------------- safe readers ---------------------------- */
const readUniformat = (o) =>
  o?.Uniformat ??
  o?.UNIFORMAT ??
  o?.Properties?.Uniformat ??
  o?.Meta?.Uniformat;

const readCategory = (o) =>
  o?.CategoryName ??
  o?.Category ??
  o?.MainCategory ??
  o?.Properties?.Category ??
  o?.Meta?.Category;

const readType = (o) =>
  o?.Type ??
  o?.TypeName ??
  o?.FamilyType ??
  o?.Properties?.Type ??
  o?.Meta?.Type;

const readFamily = (o) => o?.Family ?? o?.FamilyName ?? o?.Properties?.Family;

const readName = (o) =>
  o?.Name ??
  o?.Properties?.Name ??
  (readFamily(o) || readCategory(o) ? `${readFamily(o) || ''} ${readType(o) || ''}`.trim() : null) ??
  o?.Meta?.Name;

const readCode = (o) => o?.Code ?? o?.ItemCode ?? o?.Template?.Code;
const readCatId = (o) =>
  o?.CatalogId ?? o?.CatalogItemId ?? o?.EstimateItemId ?? o?.ItemId ?? o?.Id;
const readAwp = (o) => (o?.Awp ?? o?.AWP ?? '').toString().trim();

const readId = (o) => o?.UniqueId ?? o?.MeshId ?? o?.Id ?? o?.ElementId;

/* --------------------- BidLight AWP dictionary (inline) ---------------------
   These values mirror the platform mapping so our fallbacks match BidLight.
   (Derived from awps.json) */
const AWP_BY_DESC = {
  'concrete': '3A',
  'masonry': '4A',
  'structural steel': '5A',
  'rough carpentry': '6A',
  'millwork': '6B',
  'roofing & sheet metal': '7B',
  'insulation': '7A',
  'metal panels': '7C',
  'caulking & sealants': '7F',
  'doors frames & hardware': '8A',
  'glass & glazing': '8C',
  'framing & drywall': '9A',
  'acoustical assemblies': '9D',
  'flooring': '9E',
  'paint': '9F',
  'specialities': '10A',
  'signage': '10B',
  'window coverings': '12A',
  'special systems': '27A',
  'hvac': '23A',
  'fire protection': '21A',
  'electrical': '26A',
  'fire alarm': '26B',
  'plumbing': '22A',
  'site furnishings': '32C',
  'site utilities': '33A',
  'earthworks': '31A',
  'fencing': '32A',
  'demolition': '2A',
  // keep custom labels if present
  'hvac-d3080': 'HVAC-D3080',
  // LS CSI examples that may appear in data
  '03 - concrete': '03',
  '04 - masonry': '04',
  '05 - metals': '05',
};

/* ------------------ Revit Category → BidLight AWP map ------------------
   Covers all categories you listed. If a dataset is more specific (e.g.,
   glazing unit family), later keyword checks still catch it. */


// Exact Revit category → Bidlight AWP code
// Exact Revit category → Bidlight AWP code
const REVIT_TO_AWP = {
  'Unknown':               'GEN1',
  'Not Applicable':        'GEN1',
  'Generic Model':         'GEN1',
  'Generic Models':        'GEN1',

  // Site / Groundwork
  'Topography':            '31A',
  'Planting':              '32C',

  // Structure + concrete
  'Foundation':            '3A',
  'Foundations':           '3A',
  'Floor Structural':      '3A',
  'Floors':                '3A',
  'Rebar':                 '3A',
  'Structural Framing':    '5A',
  'Structural Columns':    '5A',
  'Column Structural':     '5A',

  // Non-structural columns (finish/casework bucket)
  'Columns':               '6A',
  'Column Non-Structural': '6A',

  // Enclosure / architectural
  'Wall':                  '9A',
  'Walls':                 '9A',
  'Roof':                  '7B',
  'Roofs':                 '7B',
  'Window':                '8C',
  'Windows':               '8C',
  'Curtain Wall Panel':    '8C',
  'Curtain Wall Panels':   '8C',
  'Curtain Wall Mullion':  '8C',
  'Curtain Wall Mullions': '8C',
  'Door':                  '8A',
  'Doors':                 '8A',

  // Finishes
  'Floor Finish':          '9E',
  'Ceiling':               '9E',
  'Ceilings':              '9E',

  // MEP rough-ins
  'Mechanical':            '23A',
  'Ducting':               '23A',
  'Ducts':                 '23A',
  'Plumbing Fixture':      '22A',
  'Piping':                '22A',
  'Sprinkler':             '21A',
  'Electrical':            '26A',

  // Tail order (trim/fixtures/specialties)
  'Lighting Fixture':      '26L',
  'Lighting Fixtures':     '26L',
  'Specialty Equipment':   '11A',
  'Furniture':             '12A',

  // Misc / non-physical metadata
  'Stairs':                '6A',
  'Railings':              '6A',
  'Mass':                  'GEN1',
  'Group Level':           'GEN1',
  'Group Room':            'GEN1',
  'Clash Visualization':   'GEN1',
  'Locator':               'GEN1',
  'Room Volume':           'GEN1',
  'Zone Volume':           'GEN1',
};

// Build a normalized lookup (original, lowercase, nospace variants)
const REVIT_TO_AWP_NORM = (() => {
  const out = {};
  for (const [k, v] of Object.entries(REVIT_TO_AWP)) {
    const variants = [
      k,
      k.toLowerCase(),
      k.replace(/\s+/g, ''),
      k.toLowerCase().replace(/\s+/g, '')
    ];
    variants.forEach(kk => { out[kk] = v; });
  }
  return out;
})();

// Keyword fallbacks (synonyms) → AWP code
const CATEGORY_AWP_MAP = {
  // Ground/site
  'site': '31A', 'grading': '31A', 'earthwork': '31A',
  'utility': '33A', 'utilities': '33A', 'landscape': '32C', 'furnishing': '32C',

  // Concrete/foundations
  'foundation': '3A', 'footing': '3A', 'pile': '3A', 'slab': '3A', 'deck': '3A',

  // Structure
  'structural': '5A', 'steel': '5A', 'frame': '5A', 'beam': '5A', 'girder': '5A', 'truss': '5A',
  'column': '5A',

  // Enclosure / arch
  'wall': '9A', 'partition': '9A', 'drywall': '9A',
  'roof': '7B', 'roofing': '7B', 'membrane': '7B',

  // Openings
  'door': '8A', 'window': '8C', 'glazing': '8C', 'mullion': '8C',

  // Finishes
  'floor finish': '9E', 'ceiling': '9E', 'finish': '9E', 'paint': '9F',

  // MEP rough-ins
  'mechanical': '23A', 'hvac': '23A', 'duct': '23A',
  'plumb': '22A', 'pipe': '22A',
  'elect': '26A', 'fire alarm': '26B', 'sprinkler': '21A',

  // Tail order
  'lighting fixture': '26L', // lights
  'appliance': '11A',        // appliances
  'specialty equipment': '11A',
  'furniture': '12A',

  //generic
  'generic': 'GEN1',
'generic model': 'GEN1',
'generic models': 'GEN1',
};


/* --------------------------- AWP sequence order ---------------------------
   Smaller = earlier. This makes schedules flow: demo → site → concrete → steel →
   walls → windows/doors → MEP rough-ins → finishes → specialties. */
const AWP_ORDER = {
  // SITE
  '31A': 10,  // Topography / grading
  '33A': 15,  // Site utilities
  '32A': 18,  // Site fencing/ancillary (if you use it)

  // FOUNDATION + STRUCTURAL FLOORS
  '3A': 20,   // Foundations + structural slabs/floors (child sort below)

  // STRUCTURE (columns → frames/beams)
  '5A': 30,

  // WALLS / ENCLOSURE
  '9A': 40,

  // ROOF
  '7B': 50, '7C': 52, '7A': 54,

  // OPENINGS (after walls): doors → windows
  '8A': 60,
  '8C': 65,

  // MEP rough-ins (after enclosure)
  '23A': 70, '22A': 72, '26A': 74, '26B': 76, '21A': 78,

  // FINISHES that should occur before trim fixtures
  '9E': 84, '9F': 86, '6A': 88, '6B': 89, '7F': 89, '10A': 89, '10B': 89, '27A': 89,

  // TAIL ORDER YOU REQUESTED
  '26L': 90,  // LIGHTS
  '11A': 95,  // APPLIANCES / SPECIALTY EQUIP
  '12A': 100, // FURNITURE — LAST

  // Landscape / site closeout
  '32C': 120,

  // Fallback buckets if they appear
  '03': 20, '04': 35, '05': 30, 'GEN1': 130,
};

const orderOf = (code) => {
  const k = (code || '').toString().toUpperCase().trim();
  if (AWP_ORDER[k] != null) return AWP_ORDER[k];
  if (/^GEN/.test(k)) return 130;     // all generics last
  if (/^\d{2}$/.test(k)) {
    // CSI section fallback e.g., "03", "05" — mid-pack if not explicitly mapped
    return AWP_ORDER[k] ?? 100;
  }
  return 110; // unknown but still before generics
};


/* --------------------------- catalog scanning --------------------------- */
const isObj = (v) => v && typeof v === 'object';

const findMeshes = (node, out = []) => {
  if (!isObj(node)) return out;

  const m = node;
  const hasId =
    ('UniqueId' in m) || ('MeshId' in m) || ('Id' in m);
  const hasGeomMeta =
    hasId && (
      ('Name' in m) ||
      ('Type' in m) ||
      ('CategoryName' in m) ||
      ('MainCategory' in m) ||
      (m?.Properties && (m.Properties.Name || m.Properties.Type || m.Properties.Category))
    );

  // NOTE: remove price/time requirement — collect every geometric element
  if (hasGeomMeta) out.push(m);

  Object.keys(node).forEach((k) => {
    const v = node[k];
    if (Array.isArray(v)) v.forEach((x) => findMeshes(x, out));
    else if (isObj(v)) findMeshes(v, out);
  });
  return out;
};


const findCatalogItemsWithAwp = (node, out = []) => {
  if (!isObj(node)) return out;
  if (typeof readAwp(node) === 'string' && readAwp(node)) out.push(node);
  Object.keys(node).forEach((k) => {
    const v = node[k];
    if (Array.isArray(v)) v.forEach((x) => findCatalogItemsWithAwp(x, out));
    else if (isObj(v)) findCatalogItemsWithAwp(v, out);
  });
  return out;
};

const makeCatalogIndex = (items) => {
  const byId = new Map();
  const byUni = new Map();
  const byCatType = new Map();
  const byName = new Map();
  const byCode = new Map();

  items.forEach((it) => {
    const id = readCatId(it);
    const uni = norm(readUniformat(it));
    const cat = norm(readCategory(it));
    const typ = norm(readType(it));
    const nm = norm(readName(it));
    const cd = norm(readCode(it));

    if (id) byId.set(String(id), it);
    if (uni) (byUni.get(uni) || byUni.set(uni, []).get(uni)).push(it);
    if (cat || typ) {
      const k = key(cat, typ);
      (byCatType.get(k) || byCatType.set(k, []).get(k)).push(it);
    }
    if (nm) (byName.get(nm) || byName.set(nm, []).get(nm)).push(it);
    if (cd) (byCode.get(cd) || byCode.set(cd, []).get(cd)).push(it);
  });

  return { byId, byUni, byCatType, byName, byCode };
};

const matchCatalogForMesh = (mesh, index) => {
  const meshCatId = readCatId(mesh);
  if (meshCatId && index.byId.has(String(meshCatId))) return index.byId.get(String(meshCatId));
  const uni = norm(readUniformat(mesh));
  if (uni && index.byUni.has(uni)) return first(index.byUni.get(uni));
  const ctKey = key(norm(readCategory(mesh)), norm(readType(mesh)));
  if (ctKey && index.byCatType.has(ctKey)) return first(index.byCatType.get(ctKey));
  const nm = norm(readName(mesh));
  if (nm && index.byName.has(nm)) return first(index.byName.get(nm));
  const cd = norm(readCode(mesh));
  if (cd && index.byCode.has(cd)) return first(index.byCode.get(cd));
  return null;
};

/* --------------------------- extraction layer --------------------------- */
const niceName = (m) => {
  const n = readName(m);
  if (n) return n;
  const fam = readFamily(m);
  const typ = readType(m);
  const cat = readCategory(m);
  const id = readId(m);
  const fallback =
    [fam, typ].filter(Boolean).join(' - ') ||
    [cat, typ].filter(Boolean).join(' - ') ||
    cat ||
    `Element ${id}`;
  return fallback;
};

export const extractMeshes = (json = {}) =>
  findMeshes(json).map((m) => {
    const laborHours = sum((m.LaborInfo || []).map((l) => Number(l.TotalTime || 0)));
    const equipHours = sum((m.EquipmentInfo || []).map((e) => Number(e.TotalTime || 0)));
    const materialCost = Number(m.TotalPrice || 0);
    const laborCost = sum((m.LaborInfo || []).map((l) => Number(l.TotalPrice || 0)));
    const equipCost = sum((m.EquipmentInfo || []).map((e) => Number(e.TotalPrice || 0)));
    const durationDays = Math.max(1, Math.ceil((laborHours + equipHours) / HOURS_PER_DAY));

    return {
      meshId: readId(m),
      name: niceName(m),
      category: readCategory(m) || 'General',
      uniformat: readUniformat(m),
      type: readType(m),
      code: readCode(m),
      laborHours,
      equipHours,
      laborCost,
      equipCost,
      materialCost,
      totalCost: laborCost + equipCost + materialCost,
      durationDays,
      mesh: m,
    };
  });

/* ------------------------- AWP assignment logic ------------------------- */
const findAwpFromDescKeywords = (text) => {
  const t = norm(text);
  let best = null;
  Object.keys(AWP_BY_DESC).some((desc) => {
    if (t.includes(norm(desc))) {
      best = AWP_BY_DESC[desc];
      return true;
    }
    return false;
  });
  return best;
};

const awpFromRevitCategory = (catRaw) => {
  if (!catRaw) return null;
  const k = String(catRaw);
  const probes = [
    k, k.toLowerCase(),
    k.replace(/\s+/g, ''), k.toLowerCase().replace(/\s+/g, '')
  ];
  for (const p of probes) {
    const hit = REVIT_TO_AWP_NORM[p];
    if (hit) return hit;
  }
  return null;
};

// Generate synthetic AWP when no catalog mapping is found


const generateSyntheticAwp = (mesh) => {
  // 1) Exact Revit category reuse
  const catRaw = readCategory(mesh);
  if (catRaw && REVIT_TO_AWP[catRaw]) return REVIT_TO_AWP[catRaw];

  // 2) Keyword/synonym fallback
  const s = norm(`${readCategory(mesh)} ${readType(mesh)} ${readName(mesh)}`);
  for (const [kw, code] of Object.entries(CATEGORY_AWP_MAP)) {
    if (s.includes(kw)) return code;
  }

  // 3) Uniformat hint (e.g., A10 → A1)
  const uni = (readUniformat(mesh) || '').toString().toUpperCase();
  const m = uni.match(/^([A-Z])(\d)/);
  if (m) return `${m[1]}${m[2]}`;

  // 4) Fallback
  return 'GEN1';
};

export const attachAwpFromCatalog = (json = {}) => {
  const meshes = extractMeshes(json);
  const catalog = findCatalogItemsWithAwp(json);
  const idx = makeCatalogIndex(catalog);

  // keep set of existing AWP codes (for diagnostics/validation)
  const existingAwpCodes = new Set();
  catalog.forEach((item) => {
    const awp = readAwp(item);
    if (awp) existingAwpCodes.add(awp);
  });

const rows = meshes.map((r) => {
  const match = matchCatalogForMesh(r.mesh, idx);
  let awpCode = (match && readAwp(match)) || null;
  if (!awpCode) awpCode = generateSyntheticAwp(r.mesh);

  // prefer explicit category from catalog match; otherwise mesh category
  const catFallback = readCategory(match) || r.category;

  return { ...r, awpCode, category: catFallback };
});


  // basic stats for debugging
  const awpStats = {};
  rows.forEach((r) => {
    awpStats[r.awpCode] = (awpStats[r.awpCode] || 0) + 1;
  });
  console.log('[AWP] assignments:', awpStats, 'total meshes:', rows.length);

  return { rows, catalogCount: catalog.length, meshCount: meshes.length };
};

/* -------------------------------- group -------------------------------- */
export const groupBy = (arr, keyName) =>
  arr.reduce((acc, it) => {
    const k = it[keyName];
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});

/* --------------------- build WBS with realistic sequencing ---------------------
   AWP groups are ordered by AWP_ORDER so construction flows correctly:
   demo → earthworks → utilities → concrete → masonry → steel → walls →
   windows/doors → MEP → finishes → specialties. */
export const buildWbsFromMeshes = (meshRows, projectStart, awpSummaryByCode = {}) => {
  const awpGroups = groupBy(meshRows, 'awpCode');

const awpCodes = Object.keys(awpGroups).sort((a, b) => {
  const oa = orderOf(a);
  const ob = orderOf(b);
  if (oa !== ob) return oa - ob;
  return String(a).localeCompare(String(b));
});



  let cursor = new Date(projectStart);
  const awpTasks = [];
  let awpIndex = 1;

  awpCodes.forEach((codeRaw) => {
    const code = codeRaw || 'UNASSIGNED';
    const items = awpGroups[codeRaw];

    // compute sums from items; prefer Division summary if present
    const duration = sum(items.map((i) => i.durationDays));
    const sumLabor = sum(items.map((i) => i.laborCost));
    const sumEquip = sum(items.map((i) => i.equipCost));
    const sumMat = sum(items.map((i) => i.materialCost));
    const sumTotal = sum(items.map((i) => i.totalCost));

    const s = awpSummaryByCode[code];
    const totalPrice = s?.total ?? sumTotal;
    const laborCost = s?.totalLab ?? sumLabor;
    const equipmentCost = s?.totalEqp ?? sumEquip;
    const materialCost = s?.totalMat ?? sumMat;
    const tolerancePct = s?.tolerancePct ?? 0;

    const start = new Date(cursor);
    const end = new Date(cursor);
    end.setDate(end.getDate() + Math.max(1, duration) - 1);

    // descriptive AWP name
    let awpName = `AWP ${code}`;
    if (!s && code !== 'UNASSIGNED' && code !== 'GEN1') {
      const cats = [...new Set(items.map((i) => i.category))].slice(0, 3);
      if (cats.length) awpName += ` (${cats.join(', ')})`;
    }

    // order children so enclosure precedes openings inside same AWP (rare),
    // and heavier elements before lighter finishes.
const norm = (s) => (s ?? '').toString().toLowerCase();

const childOrder = (x) => {
  const n = norm(`${x.category} ${x.name} ${x.type}`);

  // 3A: foundations → structural slabs/floors
  if (x.awpCode === '3A') {
    if (n.includes('foundation') || n.includes('footing') || n.includes('pile') || n.includes('pile cap') || n.includes('rebar')) return 10;
    if (n.includes('slab') || n.includes('deck') || n.includes('floor')) return 20;
  }

  // 5A: columns → braces → beams/frames
  if (x.awpCode === '5A') {
    if (n.includes('column')) return 10;
    if (n.includes('brace'))  return 20;
    if (n.includes('beam') || n.includes('girder') || n.includes('truss') || n.includes('frame')) return 30;
  }

  // 9A walls are enclosure; openings come later as separate AWPs (8A/8C)
  if (x.awpCode === '9A') return 10;

  // Openings if ever mixed: doors before windows
  if (x.awpCode === '8A') return 10;
  if (x.awpCode === '8C') return 20;

  // MEP rough-ins suggested internal order
  if (['23A','22A','26A','26B','21A'].includes(x.awpCode)) {
    const o = { '23A': 10, '22A': 20, '26A': 30, '26B': 40, '21A': 50 };
    return 100 + (o[x.awpCode] || 99);
  }

  // Tail: lights → appliances → furniture
  if (x.awpCode === '26L') return 10;
  if (x.awpCode === '11A') return 20;
  if (x.awpCode === '12A') return 30;

  // Finishes & specialties late by default
  if (['9E','9F','6A','6B','7F','10A','10B','27A'].includes(x.awpCode)) return 200;

  return 100;
};














    const sortedItems = [...items].sort((a, b) => childOrder(a) - childOrder(b));

    const awpNode = {
      id: `awp-${code}`,
      wbsCode: `1.${awpIndex}`,
      name: awpName,
      type: 'awp',
      category: items[0]?.category || 'General',
      startDate: start,
      endDate: end,
      duration: Math.max(1, duration),
      progress: 0,
      level: 2,
      parentId: 'phase-auto',
      color: '#ff6b6b',
      visible: true,
      isAWP: true,
      awpCode: code,
      tolerancePct,
      totalPrice,
      laborCost,
      equipmentCost,
      materialCost,
      meshIds: items.map((i) => i.meshId),
      children: [],
    };

    let dayOffset = 0;
    sortedItems.forEach((i, idx) => {
      const cStart = new Date(start);
      cStart.setDate(cStart.getDate() + dayOffset);
      const cEnd = new Date(cStart);
      cEnd.setDate(cEnd.getDate() + Math.max(1, i.durationDays) - 1);

      awpNode.children.push({
        id: `${i.meshId}`,
        wbsCode: `1.${awpIndex}.${idx + 1}`,
        name: i.name,
        type: 'task',
        category: i.category,
        startDate: cStart,
        endDate: cEnd,
        duration: Math.max(1, i.durationDays),
        level: 3,
        visible: true,
        totalPrice: i.totalCost,
        laborCost: i.laborCost,
        equipmentCost: i.equipCost,
        materialCost: i.materialCost,
        meshId: i.meshId,
        meshIds: [i.meshId],
        laborInfo: i.mesh?.LaborInfo || [],
        equipmentInfo: i.mesh?.EquipmentInfo || [],
        materialInfo: [
          {
            Name: i.mesh?.Type || i.mesh?.Name,
            Types: i.mesh?.MaterialNames,
            TotalAmount: i.mesh?.TotalAmount,
            TotalPrice: i.materialCost,
          },
        ],
      });

      dayOffset += Math.max(1, i.durationDays);
    });

    awpTasks.push(awpNode);
    cursor = new Date(end);
    cursor.setDate(cursor.getDate() + 1);
    awpIndex += 1;
  });

  const phase = {
    id: 'phase-auto',
    wbsCode: '1',
    name: 'Construction Schedule',
    type: 'phase',
    category: 'Program',
    startDate: awpTasks.length ? awpTasks[0].startDate : new Date(projectStart),
    endDate: awpTasks.length ? new Date(Math.max(...awpTasks.map((p) => p.endDate))) : new Date(projectStart),
    duration: awpTasks.length
      ? Math.ceil((Math.max(...awpTasks.map((p) => p.endDate)) - awpTasks[0].startDate) / (1000 * 60 * 60 * 24)) + 1
      : 1,
    progress: 0,
    level: 1,
    color: '#3498db',
    visible: true,
    expanded: true,
    totalPrice: sum(awpTasks.map((p) => p.totalPrice)),
    children: awpTasks,
  };

  return [phase];
};
